import { useEffect } from 'react';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogType?: string;
  ogUrl?: string;
  twitterCard?: string;
  jsonLd?: Record<string, unknown>[];
}

export default function SEO({
  title,
  description,
  keywords,
  canonical,
  ogTitle,
  ogDescription,
  ogType,
  ogUrl,
  twitterCard,
  jsonLd,
}: SEOProps) {
  useEffect(() => {
    document.title = title;

    const setMeta = (attrName: string, content: string, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${attrName}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, attrName);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', description);
    if (keywords) setMeta('keywords', keywords);

    if (ogTitle) setMeta('og:title', ogTitle, true);
    if (ogDescription) setMeta('og:description', ogDescription, true);
    if (ogType) setMeta('og:type', ogType, true);
    if (ogUrl) setMeta('og:url', ogUrl, true);
    if (twitterCard) setMeta('twitter:card', twitterCard);

    if (canonical) {
      let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
      if (!link) {
        link = document.createElement('link');
        link.rel = 'canonical';
        document.head.appendChild(link);
      }
      link.href = canonical;
    }

    const scriptIds: string[] = [];
    if (jsonLd && jsonLd.length > 0) {
      jsonLd.forEach((schema, index) => {
        const scriptId = `jsonld-seo-${index}`;
        scriptIds.push(scriptId);
        let script = document.getElementById(scriptId) as HTMLScriptElement | null;
        if (!script) {
          script = document.createElement('script');
          script.type = 'application/ld+json';
          script.id = scriptId;
          document.head.appendChild(script);
        }
        script.textContent = JSON.stringify(schema);
      });
    }

    return () => {
      scriptIds.forEach((id) => {
        const script = document.getElementById(id);
        if (script) script.remove();
      });
    };
  }, [title, description, keywords, canonical, ogTitle, ogDescription, ogType, ogUrl, twitterCard, jsonLd]);

  return null;
}